import { useState } from 'react';
import { FormattedMessage } from 'react-intl';

import { IconArrowDown } from '@xing-com/icons';
import { Menu, SingleSelect } from '@xing-com/menu';
import { usePopOver } from '@xing-com/pop-over';

import * as Styled from './cards-sorting.styles';
import {
  INITIAL_SORTING_ITEM,
  NewsSourcesSortingItems,
  SORTING_ITEMS,
  sortingOptionToCopyKeyMap,
} from './sorting-items';

type CardsSortingProps = {
  sortCards: (contentPageSortField: NewsSourcesSortingItems) => void;
  isLoading: boolean;
};

const SORTING_LABEL_ID = 'sorting-label';
const SORTING_BUTTON_ID = 'sorting-button';
const SORTING_MENU_ID = 'sorting-menu';
const ROLE_LISTBOX = 'listbox';

export const CardsSorting: React.FC<CardsSortingProps> = ({
  sortCards,
  isLoading,
}) => {
  const sortingMenu = usePopOver();
  const [selectedMenuItem, setSelectedMenuItem] =
    useState<NewsSourcesSortingItems>(INITIAL_SORTING_ITEM);
  const ariaControlsAttribute = sortingMenu.show
    ? { 'aria-controls': SORTING_MENU_ID }
    : {};
  const onSelectItem = (sortingItem: NewsSourcesSortingItems): void => {
    if (sortingItem !== selectedMenuItem) {
      setSelectedMenuItem(sortingItem);
      sortCards(sortingItem);
    }

    sortingMenu.handleHide();
  };

  return (
    <Styled.SortingContainer>
      <Styled.BodyCopy
        size="large"
        $isDisabled={isLoading}
        id={SORTING_LABEL_ID}
      >
        <FormattedMessage id="FOLLOWING_RECOMMENDATION_PAGE_SORTING_LABEL" />
      </Styled.BodyCopy>
      <div>
        <Styled.TextButton
          id={SORTING_BUTTON_ID}
          ref={sortingMenu.triggerRef}
          onClick={sortingMenu.togglePopOver}
          icon={IconArrowDown}
          iconAfter={true}
          size="large"
          disabled={isLoading}
          aria-labelledby={`${SORTING_LABEL_ID} ${SORTING_BUTTON_ID}`}
          aria-expanded={sortingMenu.show}
          aria-haspopup={ROLE_LISTBOX}
          {...ariaControlsAttribute}
        >
          <FormattedMessage id={sortingOptionToCopyKeyMap[selectedMenuItem]} />
        </Styled.TextButton>
        <Menu
          onOutsideClick={sortingMenu.handleHide}
          show={sortingMenu.show}
          triggerRef={sortingMenu.triggerRef}
          data-testid="recommendation-cards-sorting-menu"
          isInFlow={true}
          aria-modal="true"
        >
          <div id={SORTING_MENU_ID} role={ROLE_LISTBOX}>
            {SORTING_ITEMS.map((sortingItem) => {
              const isSelectedItem = sortingItem === selectedMenuItem;

              return (
                <SingleSelect
                  key={sortingItem}
                  onClick={() => onSelectItem(sortingItem)}
                  selected={isSelectedItem}
                  role="option"
                  aria-selected={isSelectedItem}
                >
                  <FormattedMessage
                    id={sortingOptionToCopyKeyMap[sortingItem]}
                  />
                </SingleSelect>
              );
            })}
          </div>
        </Menu>
      </div>
    </Styled.SortingContainer>
  );
};
